import { axios } from "utils";

import { ADMIN_FARMS_URL as endpoint } from "consts";

import {
  AddOrUpdateFarmFeatureFlagRequestBody,
  CooperationItemRequest,
  DataSubmissionDatesResponse,
  FarmAreaSummaryResponse,
  FarmDataSubmissionStatusAdminResponse,
  FarmFeatureFlagsResponse,
  FarmOrganicFertilizerProductionEntry,
  FieldShape,
  GetFarmFeatureFlagsRequestBody,
  OwnProductionItemRequest,
  UpdateFarmDataSubmissionAdminResponse,
} from "models";

// farms

export const getAllFarms = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}`, { data });

export const getFullFarmDetails = async (data: { id: string }) => axios.get(`${endpoint}/${data.id}`, { data });

export const getFarmAreaSummary = async ({ farmId }: { farmId: string }): Promise<FarmAreaSummaryResponse> => axios.get(`${endpoint}/${farmId}/summary`);

export const toggleTest = async (data: { farmId: string; signal: AbortSignal }) => axios.put(`${endpoint}/${data.farmId}/test`, { data });

export const getFieldShapesAdmin = async (data: { farmId: string; signal?: AbortSignal }): Promise<{ success: true; fieldShapes: FieldShape[] }> =>
  axios.get(`${endpoint}/${data.farmId}/fields/shapes`, { data });

export const getAllInvekos = async (data: { signal: AbortSignal }) => axios.get(`${endpoint}/invekos`, { data });

// pipedrive

export const updateDealById = async (data: { dealId: string; signal: AbortSignal }) => axios.put(`${endpoint}/deals/${data.dealId}`, { data });

export const linkDealToFarm = async (data: { signal: AbortSignal }) => axios.post(`${endpoint}/deals`, { data });

// data submission

export const updateFarmDataSubmissionAdmin = async (data: { farmId: string; submissionState: boolean }): Promise<UpdateFarmDataSubmissionAdminResponse> =>
  axios.put(`${endpoint}/submissions`, { data });

export const getFarmDataSubmissionStatusAdmin = async (data: { farmId: string; signal: AbortSignal }): Promise<FarmDataSubmissionStatusAdminResponse> =>
  axios.get(`${endpoint}/submissions/${data.farmId}`, { signal: data.signal });

export const updateFarmCreditAllotments = async (data: {
  farmId: string;
  errAllotment: number;
  bonusAllotmentAmount: number;
  bonusAllotmentDueDate: string;
}): Promise<FarmDataSubmissionStatusAdminResponse> => {
  const { farmId, ...rest } = data;
  return axios.put(`${endpoint}/${farmId}/credit-allotments`, rest);
};

export const getDataSubmissionDatesAdmin = async (carbonProgramId?: string): Promise<DataSubmissionDatesResponse> =>
  axios.get(`${endpoint}/data-submission-dates`, { params: { carbonProgramId } });

// footprinting toggle

export const toggleFarmFootprinting = async (data: { farmId: string; footprintingEnabled: boolean }): Promise<{ footprintingEnabled: boolean }> =>
  axios.put(`${endpoint}/footprinting`, data);

// feature flags

export const getFarmFeatureFlagsAdmin = async (data: GetFarmFeatureFlagsRequestBody): Promise<FarmFeatureFlagsResponse[]> =>
  axios.get(`${endpoint}/feature-flags/${data.farmId}`);

export const addOrUpdateFarmFeatureFlagAdmin = async (data: AddOrUpdateFarmFeatureFlagRequestBody): Promise<FarmFeatureFlagsResponse> =>
  axios.put(`${endpoint}/feature-flags`, { data });

// organic fertilizer production (admin)

export const getAdminFarmOrganicFertilizerProduction = async (data: { farmId: string; year?: number }): Promise<FarmOrganicFertilizerProductionEntry[]> =>
  axios.get(`${endpoint}/${data.farmId}/organic-fertilizer-production`, { params: data.year ? { year: data.year } : undefined });

export const createAdminFarmOrganicFertilizerProduction = async (data: {
  farmId: string;
  year: number;
  fertilizerId: string;
  sourceType?: string | null;
  livestockCount?: number | null;
  comment?: string | null;
  ownProductionItems?: OwnProductionItemRequest[];
  cooperationItems?: CooperationItemRequest[];
}): Promise<FarmOrganicFertilizerProductionEntry> => {
  const { farmId, ...body } = data;
  return axios.post(`${endpoint}/${farmId}/organic-fertilizer-production`, { data: body });
};

export const updateAdminFarmOrganicFertilizerProduction = async (data: {
  farmId: string;
  productionId: string;
  year: number;
  sourceType?: string | null;
  livestockCount?: number | null;
  comment?: string | null;
  ownProductionItems?: OwnProductionItemRequest[];
  cooperationItems?: CooperationItemRequest[];
}): Promise<FarmOrganicFertilizerProductionEntry> => {
  const { farmId, productionId, year, ...body } = data;
  return axios.put(`${endpoint}/${farmId}/organic-fertilizer-production/${productionId}`, { data: body }, { params: { year } });
};

export const deleteAdminFarmOrganicFertilizerProduction = async (data: { farmId: string; productionId: string; year: number }): Promise<{ success: boolean }> =>
  axios.delete(`${endpoint}/${data.farmId}/organic-fertilizer-production/${data.productionId}`, { params: { year: data.year } });
